/**
 * Canais — os números conectados.
 *
 * ## Esta tela responde "o que está ligado?", e só isso
 *
 * **Conectar mora em [`Conectar.tsx`](./Conectar.tsx)**, e não aqui. Até 2026-08-15 o formulário do
 * Telegram vivia no rodapé desta lista, abaixo dos cartões — quem chegava para ver o estado de um canal
 * tropeçava num formulário, e quem chegava para ligar um canal novo não tinha como saber que ele
 * existia. São duas perguntas diferentes, e a segunda ganhou tela própria com os sete canais da
 * ADR-0005, cada um com o que falta.
 *
 * O que ela responde além disso é o que **depende de cada conexão estar ligada**: o perfil do bot, no
 * Telegram, e os **templates**, no WhatsApp com a Meta. Os dois moram no cartão porque a pergunta é
 * sempre "deste número", nunca "do cliente": template pertence à conta da Meta, e um tenant com dois
 * números tem duas listas.
 *
 * O §2 da spec 04 continua valendo aqui: quem tem `:ler` e não `:gerenciar` vê os controles
 * **desabilitados com o motivo**, não escondidos — *"na tela de administração, sumir com o controle faz
 * o admin achar que o produto não tem a função"*.
 *
 * Note a diferença deliberada em relação à **barra de áreas**, onde área sem tela também aparece mas
 * como rótulo inerte: ali a pessoa está escolhendo onde ir, e um link morto é pior que um rótulo. Aqui
 * ela está numa tela de administração procurando um botão, e o botão precisa existir para ela saber que
 * a função existe.
 *
 * ## Degradado nunca é verde
 *
 * Invariante 10 do §7 da spec 02. O `/health` deste servidor é deliberadamente honesto, e a tela herda:
 * conexão `degradada` é âmbar **com o rótulo**, não um check verde.
 */

import { useId, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { falhaDe, identificadorLegivel, nomeDaConexao, useSessao, Ladrilho, MarcaHugi, type TomDoLadrilho } from "@hugi/ui";
import {
  CabecalhoDaTela,
  Carregando,
  Dica,
  Erro,
  Gaveta,
  Motivo,
  PaginaInteira,
  SemPermissao,
  Selo,
  Vazio,
  type Tom as TomDoKit,
} from "@hugi/ui/kit";
import { Mail, MessageCircle, Radio, RefreshCw, Send } from "lucide-react";
import {
  apelidarConexao,
  dispensarAquecimento,
  revogarConexao,
  removerFotoDoBot,
  salvarPerfilDoBot,
  sincronizarTemplates,
  trocarFotoDoBot,
  useConexoes,
  usePerfilDoBot,
  useTemplates,
  liberarTemplate,
  type Conexao,
  type PerfilDoBot,
  type Template,
} from "./dados.js";
import { canalDeConectar } from "./acoes-do-dono.js";
import "./canais.css";
import type { FalhaDaApi } from "@hugi/ui";

type EstadoNaTela = {
  readonly selo: "ok" | "degradado" | "ruim" | "neutro";
  readonly rotulo: string;
  readonly explicacao: string;
};

/**
 * Como a tela chama a coisa conectada, por canal.
 *
 * *"Mensagens entram e saem por este número"* é falso num bot do Telegram, que não tem número — e a
 * frase aparecia assim no cartão. Um substantivo por canal conserta todas as frases de estado de uma
 * vez, em vez de multiplicar copy por (canal × estado).
 */
const A_COISA_CONECTADA: Readonly<Record<string, string>> = {
  whatsapp: "este número",
  sms: "este número",
  telegram: "este bot",
  instagram: "esta conta",
  facebook: "esta página",
  email: "este endereço",
};

const aCoisa = (canal: string): string => A_COISA_CONECTADA[canal] ?? "este canal";

/**
 * Estado da conexão → como a tela mostra, **por canal**.
 *
 * Exaustivo e nomeado, e não um `if (estado === "ativa")`: o dia em que a migration ganhar um estado
 * novo, ele cai no `default` e **aparece com o nome cru** em vez de ser pintado de verde por
 * omissão. Verde por omissão é o defeito exato que a invariante 10 proíbe.
 *
 * ## Por que depende do canal
 *
 * As frases falavam da Meta em qualquer canal — `revogada` dizia *"retirado do lado da Meta"* para um
 * bot do Telegram, cujo acesso se perde no @BotFather e em lugar nenhum mais. Explicação errada é pior
 * que explicação ausente: manda o cliente procurar o problema no lugar errado.
 *
 * ## Os cinco são os cinco da `0004`
 *
 * `conectando | ativa | degradada | revogada | desconectada`. A versão anterior tinha **`pausada`**,
 * que o `CHECK` não aceita (código morto), e **não tinha `conectando`**, que ele aceita — então o
 * estado transitório caía no desconhecido e a tela dizia "não reconhecido por esta versão" para um
 * estado normal do produto.
 */
function estadoNaTela(
  canal: string,
  estado: string,
  confirmadaEm: string | null = "ja-confirmada",
  modo: string | null = null,
  foraDoArDesde: string | null = null,
): EstadoNaTela {
  /*
   * ⚠️ A CONFIRMAÇÃO VEM ANTES DO ESTADO, e a ordem é o conserto.
   *
   * `estado` descreve a saúde da conexão do lado da Meta; `confirmada_em` responde outra coisa — se
   * o tenant já disse que o número é dele. Uma conexão pode estar perfeitamente `ativa` na Meta e
   * **não entregar nada aqui**, porque enquanto ninguém confirma ela não resolve tenant
   * (migration `0053`).
   *
   * Sem esta ramificação o cartão dizia **"Conectada — mensagens entram e saem por este número"**
   * para um número por onde nada entra. Cartão que mente sobre o próprio estado é pior que cartão
   * ausente: ele encerra a investigação de quem foi ali justamente para descobrir por que as
   * conversas não chegam.
   */
  if (confirmadaEm === null) {
    return {
      selo: "degradado",
      rotulo: "Confirme que é seu",
      explicacao:
        "A Meta avisou que este número foi conectado, mas não diz de qual empresa ele é. " +
        "Enquanto você não confirmar, as conversas dele NÃO entram aqui.",
    };
  }

  /*
   * O VIGIA DO TRANSPORTE (contrato C3, spec 23). O banco dizia `ativa` para um número cujo socket tinha caído, e o
   * cartão mostrava "Conectada" enquanto o Início dizia "fora do ar" — duas telas se contradizendo sobre o mesmo
   * número. Quando o vigia diz que caiu, o cartão diz o mesmo. Revogada e desconectada têm frase própria, mais exata.
   */
  if (foraDoArDesde && estado !== "revogada" && estado !== "desconectada") {
    return {
      selo: "ruim",
      rotulo: "Fora do ar",
      explicacao: `Nada entra nem sai desde ${new Date(foraDoArDesde).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}. Reconecte para voltar a atender.`,
    };
  }

  switch (estado) {
    case "conectando":
      return {
        selo: "neutro",
        rotulo: "Conectando",
        // Diz o que fazer se ficar preso aqui: é o único estado que se resolve sozinho ou não se
        // resolve, e quem olha não tem como saber qual dos dois sem esperar.
        explicacao: `Ainda não entra nem sai mensagem por ${aCoisa(canal)}. Se continuar assim, refaça a conexão.`,
      };

    case "ativa":
      return { selo: "ok", rotulo: "Conectada", explicacao: `Mensagens entram e saem por ${aCoisa(canal)}.` };

    case "degradada":
      return {
        selo: "degradado",
        rotulo: "Qualidade reduzida",
        // A frase diz a CONSEQUÊNCIA, não o estado: "a Meta baixou a qualidade" não explica o que isso
        // custa a quem lê (§9 da spec 02).
        //
        // Só o WhatsApp ganha a frase da Meta, porque só ele tem nota de qualidade por número. Nos
        // outros a frase descreve o que dá para OBSERVAR, sem inventar um mecanismo que talvez não
        // exista naquele canal.
        explicacao:
          canal === "whatsapp"
            ? "A Meta reduziu a qualidade deste número. Isso diminui quantos clientes você alcança por dia."
            : `Parte das mensagens pode não chegar por ${aCoisa(canal)}. Nada foi desconectado, mas a entrega caiu.`,
      };

    case "revogada":
      return {
        selo: "ruim",
        rotulo: "Revogada",
        // Por QR (API Hugi) não há Meta no caminho: o aparelho foi desvinculado — pelo celular, pelo
        // botão de desconectar, ou pela própria Meta derrubando o vínculo. Só um QR novo resolve.
        explicacao:
          canal === "whatsapp" && modo === "nao_oficial"
            ? "O aparelho foi desvinculado deste número. Para voltar, é só ler um QR novo em “Conectar um canal”."
            : (REVOGADA[canal] ?? `O acesso a ${aCoisa(canal)} foi retirado. Reconecte para voltar a atender.`),
      };

    case "desconectada":
      return {
        selo: "ruim",
        rotulo: "Desconectada",
        explicacao: "Nada entra nem sai. Reconecte para voltar a atender.",
      };

    default:
      return { selo: "neutro", rotulo: estado, explicacao: "Estado não reconhecido por esta versão da tela." };
  }
}

/** Onde o acesso se perde, que é o que a pessoa precisa saber para recuperá-lo. */
const REVOGADA: Readonly<Record<string, string>> = {
  whatsapp: "O acesso foi retirado do lado da Meta, ou o cliente desconectou no aplicativo.",
  instagram: "O acesso foi retirado do lado da Meta, ou alguém desconectou o Hugi desta conta.",
  facebook: "O acesso foi retirado do lado da Meta, ou alguém desconectou o Hugi desta página.",
  // Consistente com a recusa da rota de envio: o token some no @BotFather, não na Meta.
  telegram: "O token deste bot não vale mais. Acontece quando alguém gera outro no @BotFather.",
};

/**
 * Canal → o nome próprio dele.
 *
 * Era `className="capitalize"` sobre o valor do banco, e `text-transform` erra em três dos seis
 * canais da `0004`: `whatsapp` vira "Whatsapp", `sms` vira "Sms", `email` vira "Email". Nome de
 * produto não é capitalização, é grafia — e o fallback devolve o valor cru, que é o certo para um
 * canal que esta versão da tela não conhece.
 */
const CANAIS: Readonly<Record<string, string>> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram Direct",
  facebook: "Facebook Messenger",
  telegram: "Telegram",
  sms: "SMS",
  email: "E-mail",
};

const MODOS: Readonly<Record<string, string>> = {
  // A chave é `coex`, como a `0004` grava — era `coexistencia`, que nunca casou, e uma conexão de
  // coexistência caía no fallback e mostrava a palavra crua `coex` ao cliente.
  coex: "Coexistência — o mesmo número no celular e aqui",
  /*
    ⚠️ "API Hugi" é o NOME DO TRANSPORTE, e ele não muda quando a tecnologia muda.
    Decisão do owner em 2026-09-03: *"independente se Baileys, whatsmeow, ou qualquer outra coisa
    que eu usar, vamos sempre chamar de API Hugi"*.

    O que estava aqui — "Aparelho vinculado, por QR Code" — descrevia o MEIO de conectar, não o
    transporte. Ele já mudou uma vez (Baileys → whatsmeow, ADR-0010) sem que o cliente precisasse
    saber, e o rótulo antigo prometia um detalhe de implementação para sempre.

    ⚠️ NÃO CONFUNDIR com o **plano Hugi API** (R$ 800/número, spec 16) nem com a **API do Hugi**
    (as rotas `/api/v1`, `docs/ajuda/api-do-hugi.md`). São três nomes vizinhos, e o owner sabe:
    ele manteve o nome do plano de propósito. Se algum dia um deles for renomeado, é este
    comentário que diz quais eram os três.
  */
  nao_oficial: "API Hugi",
};

/**
 * O modo depende do CANAL, não só do modo.
 *
 * `canal_conexoes.modo` só aceita `oficial | coex | nao_oficial` (`0004`), então **todo** canal novo
 * entra como `oficial` — inclusive o Telegram, que não tem nada a ver com a Meta. Traduzir só pelo
 * modo fazia o cartão de um bot do Telegram dizer "API oficial da Meta": falso, na tela do cliente.
 *
 * O conserto é aqui e não numa migration de propósito. `oficial` no banco quer dizer "pela API do
 * dono do canal", e isso está certo para os quatro; quem sabe quem é o dono é a tela.
 */
const API_OFICIAL_DE: Readonly<Record<string, string>> = {
  whatsapp: "API oficial da Meta",
  instagram: "API oficial da Meta",
  facebook: "API oficial da Meta",
  telegram: "API oficial do Telegram",
};

function rotuloDoModo(canal: string, modo: string): string {
  // Sem dono conhecido, a frase genérica — e não o nome de uma empresa que talvez não seja a dona.
  if (modo === "oficial") return API_OFICIAL_DE[canal] ?? "API oficial do canal";
  return MODOS[modo] ?? modo;
}

/**
 * Esta conexão tem templates?
 *
 * **Template é da conta da Meta (a WABA), não do número** — e por isso a pergunta não é "é WhatsApp",
 * é "fala com a Meta". Um número por aparelho vinculado não tem WABA: `sincronizarTemplatesDaConexao`
 * o recusa com `conexao_sem_waba`, e oferecer ali um botão cujo único desfecho possível é a recusa
 * seria pior que não oferecer nada.
 *
 * A ausência aqui **não é a mesma coisa** que o controle desabilitado do §2 da spec 04: ali a função
 * existe e a pessoa não pode usá-la; aqui o conceito não existe naquele canal. É a mesma distinção
 * que faz "Editar perfil" aparecer só no Telegram.
 */
function temTemplates(c: Conexao): boolean {
  return c.canal === "whatsapp" && c.modo !== "nao_oficial";
}

/**
 * O estado do template no vocabulário de quem administra, não no da Meta.
 *
 * Os mesmos sete de `MAPA_DE_ESTADO` em `canal/oficial/templates.ts`, e as mesmas frases do inbox: o
 * atendente e o administrador leem a mesma palavra para o mesmo estado. O `default` devolve o valor
 * cru — `LIMIT_EXCEEDED` e `DELETED` caem em `outro` de propósito lá, e traduzi-los por analogia aqui
 * inventaria um significado.
 */
const ESTADO_DO_TEMPLATE: Readonly<Record<string, string>> = {
  aprovado: "Aprovado",
  pendente: "Em análise pela Meta",
  recusado: "Recusado pela Meta",
  pausado: "Pausado por qualidade",
  desativado: "Desativado",
  ausente: "Não existe mais na Meta",
  outro: "Estado não reconhecido",
};

/** Aprovado é o único que serve para enviar; o resto é diagnóstico, e não pode parecer sucesso. */
const SELO_DO_TEMPLATE: Readonly<Record<string, "ok" | "degradado" | "ruim" | "neutro">> = {
  aprovado: "ok",
  pendente: "neutro",
  recusado: "ruim",
  pausado: "degradado",
  desativado: "ruim",
  ausente: "ruim",
};

/*
 * REVOGADA É HISTÓRICO, NÃO CANAL (owner, 03/09: "eu queria que as revogadas não aparecessem").
 * A tela existe para dizer por onde as conversas ENTRAM; uma conexão revogada não entra nada, e
 * cada cartão dela empurra os vivos para baixo. Ficam ocultas por padrão, atrás de um interruptor
 * que lembra a escolha — preferência de pessoa, não de sessão, o mesmo critério da barra lateral.
 *
 * Duas exceções, de propósito: se TODAS são revogadas, elas aparecem (esconder tudo mostraria
 * "Nenhum canal conectado", que é mentira: há canais, só que mortos); e se não há nenhuma, o
 * interruptor não aparece — controle que não muda nada é ruído.
 */
const CHAVE_DE_REVOGADAS = "hugi.canais.mostrar-revogadas";

function lerPreferenciaDeRevogadas(): boolean {
  try {
    return localStorage.getItem(CHAVE_DE_REVOGADAS) === "1";
  } catch {
    return false; // janela privada ou cookie bloqueado: o padrão (ocultas) é o certo
  }
}

function guardarPreferenciaDeRevogadas(mostrar: boolean): void {
  try {
    localStorage.setItem(CHAVE_DE_REVOGADAS, mostrar ? "1" : "0");
  } catch {
    // sem persistência a escolha vale só até recarregar — melhor que quebrar a tela
  }
}

/**
 * Um botão que a sessão não pode usar: desabilitado, e o MOTIVO ao lado (§2 da spec 04 — sumir com o
 * controle faz o admin achar que o produto não tem a função; o kit da v2 pede o motivo à vista, ligado
 * ao botão por `aria-describedby`, e não só no `title`, que o celular não mostra).
 */
function BotaoComMotivo(props: { readonly classe: string; readonly motivo: string; readonly children: ReactNode }) {
  const id = useId();
  return (
    <span className="hugi-canais-com-motivo">
      <Motivo id={id}>{props.motivo}</Motivo>
      <button type="button" className={props.classe} disabled aria-describedby={id} title={props.motivo}>
        {props.children}
      </button>
    </span>
  );
}

const NO_KIT: Readonly<Record<EstadoNaTela["selo"], TomDoKit>> = { ok: "sucesso", degradado: "atencao", ruim: "perigo", neutro: "neutro" };

/**
 * O (i) do cabeçalho. A frase de baixo era um parágrafo no rodapé da lista — dito na tela, e não só no
 * código, porque é a informação que evita o chamado "por que não vejo o token aqui?" (§11 da spec 02: o
 * front recebe estado, nunca segredo).
 */
const DICA_DA_TELA =
  "Os números e contas por onde as conversas entram. Nenhuma credencial aparece nesta tela. " +
  "Token de canal fica no servidor, e a API do painel não o devolve.";

/**
 * Canais na família **Consulta** da v2 (spec 22, 31/10): a página não rola; os cartões dos números rolam
 * dentro da própria região. O que é longo — a lista de templates e o perfil do bot — mora numa `Gaveta`.
 */
export function Canais() {
  const sessao = useSessao();
  const conexoes = useConexoes();
  const podeConectar = sessao.permissoes.includes("connect_channel");

  /*
   * Os templates de TODAS as conexões, numa consulta só, e não uma por cartão.
   *
   * A rota lê `canal_templates` — o nosso espelho —, não a Meta: buscar a lista inteira custa uma
   * consulta, e agrupar por conexão é trabalho da tela. Ligada só quando há conexão que tem template
   * e a sessão tem a permissão da rota; ver `BlocoDeTemplates` para o que aparece quando não tem.
   */
  const temConexaoComTemplate = (conexoes.data?.conexoes ?? []).some(temTemplates);
  const podeLerTemplates = sessao.permissoes.includes("read_conversation");
  const templates = useTemplates(temConexaoComTemplate && podeLerTemplates);
  const [mostrarRevogadas, setMostrarRevogadas] = useState(lerPreferenciaDeRevogadas);

  const todas = conexoes.data?.conexoes ?? [];
  const revogadas = todas.filter((c) => c.estado === "revogada");
  const lista = mostrarRevogadas || revogadas.length === todas.length ? todas : todas.filter((c) => c.estado !== "revogada");

  const cabecalho = (
    <CabecalhoDaTela
      icone={<Radio size={18} />}
      titulo="Canais"
      dica={DICA_DA_TELA}
      ferramentas={
        /* Só existe quando muda alguma coisa: com revogadas E vivas (ver `CHAVE_DE_REVOGADAS`). */
        revogadas.length > 0 && revogadas.length < todas.length ? (
          <label className="hugi-canais-revogadas">
            <input
              type="checkbox"
              role="switch"
              className="v2-interruptor"
              checked={mostrarRevogadas}
              onChange={(e) => {
                setMostrarRevogadas(e.target.checked);
                guardarPreferenciaDeRevogadas(e.target.checked);
              }}
            />
            Mostrar revogadas ({revogadas.length})
          </label>
        ) : null
      }
      /*
        A saude do numero (spec 19) entra como acao SECUNDARIA desta tela, e nao como area propria na
        barra: a pergunta dela ("posso aumentar o volume?") so' existe depois de haver numero conectado,
        e quem a faz esta' justamente olhando a lista de numeros.
      */
      secundarias={<Link href="/canais/saude" className="v2-botao">Saúde do número</Link>}
      primaria={
        podeConectar ? (
          <Link href="/canais/conectar" className="v2-botao is-primario">Conectar um canal</Link>
        ) : (
          <BotaoComMotivo classe="v2-botao is-primario" motivo="Conectar exige a permissão connect_channel.">
            Conectar um canal
          </BotaoComMotivo>
        )
      }
    />
  );

  let corpo: ReactNode;
  if (conexoes.isPending) corpo = <Carregando />;
  else if (conexoes.error) {
    const falha = falhaDe(conexoes.error);
    corpo = falha?.tipo === "sem_permissao"
      ? <SemPermissao>{falha.permissao ? `Ver os canais exige a permissão ${falha.permissao}.` : undefined}</SemPermissao>
      : <Erro aoTentarDeNovo={() => void conexoes.refetch()}>{falha?.erro.mensagem ?? "Não deu para carregar os canais."}</Erro>;
  } else if (lista.length === 0) {
    /*
     * O vazio manda para o fluxo, e não explica a arquitetura.
     *
     * A versão anterior citava Embedded Signup e `npm run nao-oficial` — dois nomes que não
     * significam nada para quem acabou de assinar, e um deles um comando de terminal que o
     * cliente não tem como rodar. O que ele precisa saber é onde clicar.
     */
    corpo = (
      <Vazio>
        <strong>Nenhum canal conectado</strong> Enquanto não houver canal, nenhuma conversa chega aqui. Conectar leva
        um minuto: use o botão “Conectar um canal” acima.
      </Vazio>
    );
  } else {
    corpo = (
      /*
        Grade fluida, e não pilha: cinco conexões eram 1.313px de rolagem numa coluna de 720px. A grade
        rola DENTRO da região (a página não), e cada cartão mostra o FATO; a prosa está atrás de um
        (i) ou de uma gaveta.
      */
      <div className="v2-elastico hugi-canais-regiao" role="region" aria-label="Conexões" tabIndex={0}>
        <ul className="hugi-canais-grade">
          {lista.map((c) => (
            <li key={c.id}>
              <CartaoDeConexao
                conexao={c}
                templates={{
                  carregando: templates.isPending && temConexaoComTemplate && podeLerTemplates,
                  podeLer: podeLerTemplates,
                  lista: (templates.data?.templates ?? []).filter((t) => t.conexaoId === c.id),
                }}
              />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <PaginaInteira rotulo="Canais" classe="hugi-canais">
      {cabecalho}
      {corpo}
    </PaginaInteira>
  );
}

/** O que a tela de templates de uma conexão precisa saber, resolvido uma vez lá em cima. */
interface TemplatesDoCartao {
  readonly carregando: boolean;
  readonly podeLer: boolean;
  readonly lista: readonly Template[];
}

/** O tom do ladrilho é (canal, modo): a API Hugi é a marca; o oficial é o verde do WhatsApp. */
function tomDaConexao(c: Conexao): TomDoLadrilho {
  if (c.canal === "whatsapp") return c.modo === "nao_oficial" ? "hugi" : "wa";
  if (c.canal === "telegram") return "tg";
  if (c.canal === "email") return "em";
  if (c.canal === "sms") return "sm";
  return "neutro";
}

function iconeDaConexao(c: Conexao) {
  if (c.canal === "whatsapp") return c.modo === "nao_oficial" ? <MarcaHugi /> : <MessageCircle size={19} />;
  if (c.canal === "telegram") return <Send size={19} />;
  if (c.canal === "email") return <Mail size={19} />;
  return <Radio size={19} />;
}

function CartaoDeConexao(props: { readonly conexao: Conexao; readonly templates: TemplatesDoCartao }) {
  const { conexao: c } = props;
  const sessao = useSessao();
  const podeIntervir = sessao.permissoes.includes("connect_channel");
  const [perfilAberto, setPerfilAberto] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [revogando, setRevogando] = useState(false);
  const [apelidando, setApelidando] = useState(false);
  const [avisos, setAvisos] = useState<readonly string[]>([]);
  // O AQUECIMENTO (0096). Dispensar pede confirmação — o teto é o que protege o chip novo; voltar a aquecer não.
  const [confirmandoAquecimento, setConfirmandoAquecimento] = useState(false);
  const [gravandoAquecimento, setGravandoAquecimento] = useState(false);
  const cliente = useQueryClient();

  // `foraDoArDesde` (C3) ainda pode não vir de um servidor mais velho: sem ele, vale o estado do banco.
  const foraDoArDesde = (c as Conexao & { readonly foraDoArDesde?: string | null }).foraDoArDesde ?? null;
  const estado = estadoNaTela(c.canal, c.estado, c.confirmadaEm, c.modo, foraDoArDesde);
  // RECONECTAR (spec 23, jornada 4): quando nada entra nem sai, o verbo do cartão é voltar — no canal DELE.
  const precisaReconectar = estado.rotulo === "Fora do ar" || c.estado === "revogada" || c.estado === "desconectada";

  async function gravarAquecimento(dispensar: boolean) {
    setGravandoAquecimento(true);
    const r = await dispensarAquecimento(cliente, c.id, dispensar);
    setGravandoAquecimento(false);
    setConfirmandoAquecimento(false);
    if (!r.ok) setAvisos([r.falha ? mensagemDaFalha(r.falha) : "Não deu para gravar o aquecimento."]);
  }

  async function revogar() {
    setRevogando(true);
    const r = await revogarConexao(cliente, c.id);
    setRevogando(false);
    setConfirmando(false);
    // Aviso NÃO é erro, e por isso não vira mensagem vermelha de falha: a revogação aconteceu, e o
    // que sobrou foi limpeza do lado do Telegram. Esconder isso deixaria uma ponta pendurada sem
    // ninguém saber.
    setAvisos(r.ok ? (r.avisos ?? []) : [mensagemDaFalha(r.falha)]);
  }

  /*
    O aquecimento mora NA FICHA, em linhas curtas, e o controle dele também: na fila de ações ele era o
    terceiro botão e jogava o "Revogar" para uma segunda linha — medido na bancada em 11/09.
  */
  const aq = c.aquecimento ?? null;

  return (
    <article className="v2-cartao hugi-canais-cartao">
      <header className="hugi-canais-topo">
        <Ladrilho tom={tomDaConexao(c)}>{iconeDaConexao(c)}</Ladrilho>
        <div className="hugi-canais-nome">
          {/*
            O título é o APELIDO quando há, senão o número — pela mesma máscara do inbox (`@hugi/ui`).
            A linha de baixo é o MODO ("API Hugi · WhatsApp"), que é a informação que o ladrilho anuncia
            por cor e o texto confirma.
          */}
          <h2>{nomeDaConexao(c)}</h2>
          <p>
            {rotuloDoModo(c.canal, c.modo)}
            {c.apelido ? ` · ${identificadorLegivel(c.numeroExibicao ?? "")}` : ""}
          </p>
        </div>
        <Selo tom={NO_KIT[estado.selo]}>{estado.rotulo}</Selo>
      </header>

      {apelidando ? (
        <EditorDeApelido conexaoId={c.id} atual={c.apelido ?? ""} aoFechar={() => setApelidando(false)} aoFalhar={(m) => setAvisos([m])} />
      ) : null}

      {/*
        A frase do estado só aparece quando há algo a dizer além do selo: "Conectada" + "Mensagens
        entram e saem por este número" eram duas fontes para a mesma verdade. Pendente e revogada
        continuam explicadas — ali a frase é a informação.
      */}
      {estado.selo !== "ok" ? <p className="hugi-canais-explicacao">{estado.explicacao}</p> : null}

      <dl className="hugi-canais-ficha">
        {c.conectadaEm ? (
          <div>
            <dt>Conectada em</dt>
            <dd>{new Date(c.conectadaEm).toLocaleDateString("pt-BR")}</dd>
          </div>
        ) : null}
        <div>
          <dt>
            Carência de reabertura{" "}
            <Dica texto="Cliente que volta a escrever dentro desse prazo reabre o mesmo atendimento em vez de abrir um novo — é o parâmetro que decide se o relatório conta uma conversa ou duas." />
          </dt>
          <dd>{`${c.carenciaReaberturaMin} min`}</dd>
        </div>
        {aq ? (
          <>
            <div>
              <dt>
                Aquecimento{" "}
                <Dica texto="Número novo publicando no ritmo cheio é o que o WhatsApp mais pune. Na primeira semana, o teto de publicações em grupo sobe a cada dia; depois dela, vale só o ritmo de cada publicação." />
              </dt>
              <dd>{aq.dispensadoEm ? `dispensado em ${new Date(aq.dispensadoEm).toLocaleDateString("pt-BR")}` : `dia ${aq.dia + 1} de ${aq.dias}`}</dd>
            </div>
            {aq.dispensadoEm ? null : (
              <div>
                <dt>Publicações hoje</dt>
                <dd>{`${aq.enviadosHoje} de ${aq.teto}`}</dd>
              </div>
            )}
            {podeIntervir && !confirmandoAquecimento ? (
              <div>
                <dt className="v2-sr">Aquecimento deste número</dt>
                <dd>
                  <button
                    type="button"
                    className="v2-link"
                    disabled={gravandoAquecimento}
                    onClick={() => (aq.dispensadoEm ? void gravarAquecimento(false) : setConfirmandoAquecimento(true))}
                  >
                    {aq.dispensadoEm ? "Voltar a aquecer" : "Dispensar aquecimento"}
                  </button>
                </dd>
              </div>
            ) : null}
          </>
        ) : null}
      </dl>

      {aq && confirmandoAquecimento ? (
        <div className="hugi-canais-confirmar">
          <span className="hugi-canais-atencao">Ele publica no ritmo cheio desde hoje.</span>
          <span className="hugi-canais-botoes">
            <button type="button" className="v2-botao is-pequeno is-perigo" disabled={gravandoAquecimento} onClick={() => void gravarAquecimento(true)}>Dispensar</button>
            <button type="button" className="v2-botao is-pequeno" onClick={() => setConfirmandoAquecimento(false)}>Cancelar</button>
          </span>
        </div>
      ) : null}

      <div className="hugi-canais-acoes">
        {/*
          "Editar perfil" fica AQUI, junto das outras ações do cartão, e não como um link no rodapé: a
          primeira versão era um link de texto embaixo da lista de campos, e o primeiro usuário real do
          produto — o dono — não o achou. Controle que ninguém encontra é função que não existe.
        */}
        {precisaReconectar ? (
          podeIntervir ? (
            <Link href={canalDeConectar(c.canal, c.modo)} className="v2-botao is-pequeno">
              <RefreshCw size={14} aria-hidden="true" /> Reconectar
            </Link>
          ) : (
            <BotaoComMotivo classe="v2-botao is-pequeno" motivo="Reconectar exige a permissão connect_channel.">Reconectar</BotaoComMotivo>
          )
        ) : null}
        <button type="button" className="v2-botao is-pequeno" onClick={() => setApelidando((a) => !a)}>
          {apelidando ? "Fechar" : c.apelido ? "Mudar apelido" : "Dar apelido"}
        </button>
        {c.canal === "telegram" ? (
          <button type="button" className="v2-botao is-pequeno" onClick={() => setPerfilAberto(true)}>Editar perfil</button>
        ) : null}

        {/*
          Revogar (§5.2 da spec 04). **Confirmação antes**, e não `onClick` direto: revogar apaga a
          credencial do cofre, e reconectar exige o token de novo. É a ação menos reversível desta tela.
        */}
        {confirmando ? null : podeIntervir ? (
          <button type="button" className="v2-botao is-pequeno is-perigo" disabled={revogando} onClick={() => setConfirmando(true)}>Revogar</button>
        ) : (
          <BotaoComMotivo classe="v2-botao is-pequeno is-perigo" motivo="Revogar exige a permissão connect_channel.">Revogar</BotaoComMotivo>
        )}
      </div>

      {confirmando ? (
        <div className="hugi-canais-confirmar">
          <p>
            Revogar desliga {aCoisa(c.canal)} e <strong>apaga a credencial</strong>. Para voltar a usar, será preciso
            conectar de novo.
          </p>
          <span className="hugi-canais-botoes">
            <button type="button" className="v2-botao is-pequeno is-perigo" onClick={() => void revogar()}>{revogando ? "Revogando…" : "Revogar"}</button>
            <button type="button" className="v2-botao is-pequeno" onClick={() => setConfirmando(false)}>Cancelar</button>
          </span>
        </div>
      ) : null}

      {avisos.length ? (
        <ul className="hugi-canais-avisos">
          {avisos.map((a) => <li key={a}>{a}</li>)}
        </ul>
      ) : null}

      {temTemplates(c) ? <BlocoDeTemplates conexao={c} templates={props.templates} /> : null}

      {c.canal === "telegram" ? (
        <PerfilDoBotDeTelegram conexao={c} podeEditar={podeIntervir} aberto={perfilAberto} aoFechar={() => setPerfilAberto(false)} />
      ) : null}
    </article>
  );
}

/**
 * Templates — a lista da Meta, espelhada aqui.
 *
 * ## Por que esta é a tela que faltava
 *
 * `POST /conexoes/:id/templates/sincronizar` existe desde o H10, com `manage_template`, e **nenhum
 * arquivo de `web/` a chamava**. O Início mostrava um botão "Sincronizar templates" que era um `Link`
 * para cá — navegava e não sincronizava. Só aparece quando alguém procura o botão.
 *
 * ## A Meta é a fonte; isto é espelho
 *
 * Não há criar, editar nem submeter template aqui, e a falta é dita (no (i)) em vez de sugerida pela
 * ausência: quem aprova é a Meta. A única escrita é ir buscar.
 *
 * ## Os não aprovados aparecem, com o estado
 *
 * Esconder o que está `pendente` faria quem acabou de submeter concluir que a sincronização não
 * funcionou. E `ausente` é exatamente o que a `0011` guarda para não desaparecer calado.
 *
 * No cartão fica o FATO (quantos, quantos aprovados) e o botão; a lista mora na gaveta (spec 22).
 */
const DICA_DOS_TEMPLATES =
  "As mensagens aprovadas pela Meta. São as únicas que alcançam um cliente que não escreve há mais de 24 horas. " +
  "Criar e submeter template continua sendo na Meta. O Hugi espelha a lista: o que está aqui é o que existe lá na última sincronização.";

function BlocoDeTemplates(props: { readonly conexao: Conexao; readonly templates: TemplatesDoCartao }) {
  const { conexao: c, templates: t } = props;
  const sessao = useSessao();
  const cliente = useQueryClient();
  const podeSincronizar = sessao.permissoes.includes("manage_template");

  const [sincronizando, setSincronizando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [aberta, setAberta] = useState(false);

  const aprovados = t.lista.filter((x) => x.estado === "aprovado");

  async function sincronizar() {
    setSincronizando(true);
    setRecado(null);
    const r = await sincronizarTemplates(cliente, c.id);
    setSincronizando(false);
    setRecado(
      r.ok
        ? {
            tipo: "ok",
            // O número, e não "pronto": sincronizar e receber zero é um desfecho legítimo. A
            // concordância do segundo trecho é feita à mão porque "1 deixaram" já apareceu na tela.
            texto:
              `${r.gravados} template(s) vieram da Meta` +
              (r.ausentes ? (r.ausentes === 1 ? ", e 1 deixou de existir lá." : `, e ${r.ausentes} deixaram de existir lá.`) : "."),
          }
        : { tipo: "erro", texto: mensagemDaFalha(r.falha, "Não deu para falar com a Meta. Tente de novo.") },
    );
  }

  return (
    <div className="hugi-canais-templates">
      <div className="hugi-canais-templates-topo">
        <h3>
          Templates <Dica texto={DICA_DOS_TEMPLATES} />{" "}
          <span className="hugi-canais-fraco">
            {t.podeLer && !t.carregando ? (t.lista.length === 0 ? "— nenhum" : `— ${t.lista.length}, ${aprovados.length} aprovado(s)`) : ""}
          </span>
        </h3>
        <span className="hugi-canais-botoes">
          <button type="button" className="v2-botao is-pequeno" onClick={() => setAberta(true)}>Ver templates</button>
          {/*
            O botão que a rota esperava. Desabilitado com o motivo para quem não tem a permissão — §2 da
            spec 04 —, e nunca ausente: sumir com ele faria o administrador achar que o Hugi não busca
            template nenhum.
          */}
          {podeSincronizar ? (
            <button type="button" className="v2-botao is-pequeno" disabled={sincronizando} onClick={() => void sincronizar()}>
              {sincronizando ? "Buscando na Meta…" : "Sincronizar com a Meta"}
            </button>
          ) : (
            <BotaoComMotivo classe="v2-botao is-pequeno" motivo="Sincronizar exige a permissão manage_template.">Sincronizar com a Meta</BotaoComMotivo>
          )}
        </span>
      </div>

      {recado ? (
        <p className={recado.tipo === "ok" ? "hugi-canais-recado" : "hugi-canais-recado is-erro"} role={recado.tipo === "erro" ? "alert" : "status"}>
          {recado.texto}
        </p>
      ) : null}

      <Gaveta aberta={aberta} aoFechar={() => setAberta(false)} titulo={`Templates — ${nomeDaConexao(c)}`} largura="larga">
        <div className="hugi-canais-gaveta">
          {!t.podeLer ? (
            /*
              A lista tem permissão própria (`read_conversation`, porque quem a consome no dia a dia é o
              atendente). Dizer qual falta é diferente de mostrar lista vazia.
            */
            <SemPermissao>Ver a lista exige a permissão read_conversation. Sincronizar não depende dela.</SemPermissao>
          ) : t.carregando ? (
            <Carregando />
          ) : t.lista.length === 0 ? (
            <Vazio>
              Nenhum template nesta conexão ainda. Eles são criados e aprovados <strong>na Meta</strong> — no WhatsApp
              Manager ou pela API —, e o botão “Sincronizar com a Meta” traz a lista de lá para cá.
            </Vazio>
          ) : (
            <>
              {aprovados.length === 0 ? (
                <p className="hugi-canais-atencao">
                  Nenhum aprovado ainda — só template aprovado alcança quem está fora da janela de 24 horas. Quem aprova é
                  a Meta, e o prazo é dela.
                </p>
              ) : null}
              <ul className="hugi-canais-lista-templates">
                {t.lista.map((x) => (
                  <li key={x.id}>
                    <span className="hugi-canais-selos">
                      <code>{x.nome}</code>
                      <Selo tom="neutro">{x.idioma}</Selo>
                      <Selo tom={NO_KIT[SELO_DO_TEMPLATE[x.estado] ?? "neutro"]}>{ESTADO_DO_TEMPLATE[x.estado] ?? x.estado}</Selo>
                      {/* Marketing é sempre cobrado pela Meta, mesmo dentro da janela — dito onde o template aparece. */}
                      {x.sempreCobrado ? <Selo tom="atencao">sempre cobrado</Selo> : null}
                      {/*
                        A saúde do template (spec 17 §5a): a qualidade que a Meta mede, e o bloqueio DENTRO
                        do Hugi. Liberar é manual, e fica AQUI, ao lado do motivo.
                      */}
                      {x.qualidade && x.qualidade !== "UNKNOWN" ? (
                        <Selo tom={x.qualidade === "GREEN" ? "sucesso" : x.qualidade === "YELLOW" ? "atencao" : "perigo"}>{`qualidade ${x.qualidade}`}</Selo>
                      ) : null}
                      {x.bloqueio ? <Selo tom="perigo">{`bloqueado: ${x.bloqueio.motivo}`}</Selo> : null}
                    </span>
                    {x.corpo ? <span className="hugi-canais-fraco">{x.corpo}</span> : null}
                    {x.bloqueio ? (
                      <span className="hugi-canais-bloqueio">
                        <span className="hugi-canais-atencao">
                          {x.bloqueio.detalhe ?? "Bloqueado no Hugi."}
                          {x.bloqueio.categoriaAnterior ? ` Era ${x.bloqueio.categoriaAnterior}.` : ""} Campanhas com ele estão pausadas até alguém liberar.
                        </span>
                        {podeSincronizar ? (
                          <button
                            type="button"
                            className="v2-botao is-pequeno"
                            onClick={() => {
                              if (window.confirm(`Liberar o template ${x.nome}? Quem libera assume a tarifa e a qualidade atuais.`)) {
                                void liberarTemplate(cliente, x.id).then((r) => {
                                  if (!r.ok) setRecado({ tipo: "erro", texto: mensagemDaFalha(r.falha, "Não deu para liberar.") });
                                });
                              }
                            }}
                          >
                            Liberar
                          </button>
                        ) : (
                          <BotaoComMotivo classe="v2-botao is-pequeno" motivo="Liberar exige a permissão manage_template.">Liberar</BotaoComMotivo>
                        )}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </Gaveta>
    </div>
  );
}

/**
 * Perfil do bot — o que dá para editar sem sair do Hugi, numa gaveta.
 *
 * ## Só monta quando abre, e por isso lê só quando abre
 *
 * São quatro chamadas à Bot API por conexão. Buscá-las ao abrir a tela de Canais faria quem só queria
 * conferir se o número está conectado pagar o custo de um formulário que não pediu.
 *
 * ## O que é do @BotFather aparece, desabilitado, com o motivo
 *
 * Username, token e privacidade não têm método na Bot API — e a regra do §2 da spec 04 vale aqui: o
 * campo fica visível dizendo onde se muda.
 */
function PerfilDoBotDeTelegram(props: {
  readonly conexao: Conexao;
  readonly podeEditar: boolean;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}) {
  return (
    <Gaveta aberta={props.aberto} aoFechar={props.aoFechar} titulo="Perfil do bot" largura="larga">
      {props.aberto ? <FormularioDoPerfil conexao={props.conexao} podeEditar={props.podeEditar} /> : null}
    </Gaveta>
  );
}

function FormularioDoPerfil(props: { readonly conexao: Conexao; readonly podeEditar: boolean }) {
  const { conexao: c, podeEditar } = props;
  const cliente = useQueryClient();
  const perfil = usePerfilDoBot(c.id, true);
  const idDoMotivo = useId();

  const [rascunho, setRascunho] = useState<PerfilDoBot | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  const dados = perfil.data?.perfil;
  const limites = perfil.data?.limites;
  // O rascunho nasce do que está NO AR, e não vazio: formulário de edição em branco afirma "não há
  // descrição", e salvar por cima apagaria o que o cliente nunca viu.
  const atual = rascunho ?? dados ?? null;

  const mudou =
    atual !== null &&
    dados !== undefined &&
    (atual.nome !== dados.nome || atual.descricao !== dados.descricao || atual.descricaoCurta !== dados.descricaoCurta);

  async function salvar() {
    if (!atual) return;
    setSalvando(true);
    setRecado(null);
    const r = await salvarPerfilDoBot(cliente, c.id, { nome: atual.nome, descricao: atual.descricao, descricaoCurta: atual.descricaoCurta });
    setSalvando(false);
    if (r.ok) {
      setRascunho(null);
      setRecado({ tipo: "ok", texto: "Salvo. O Telegram já está mostrando o novo perfil." });
    } else {
      setRecado({ tipo: "erro", texto: mensagemDaFalha(r.falha) });
    }
  }

  async function enviarFoto(arquivo: File) {
    setSalvando(true);
    setRecado(null);
    const r = await trocarFotoDoBot(c.id, arquivo);
    setSalvando(false);
    setRecado(r.ok ? { tipo: "ok", texto: "Foto trocada. Pode levar alguns minutos para aparecer em todo lugar." } : { tipo: "erro", texto: mensagemDaFalha(r.falha) });
  }

  if (perfil.isPending) return <Carregando />;
  if (perfil.error) {
    return <Erro aoTentarDeNovo={() => void perfil.refetch()}>{falhaDe(perfil.error)?.erro.mensagem ?? "Não deu para ler o perfil do bot."}</Erro>;
  }
  if (!atual) return null;

  const motivo = !podeEditar ? "Editar exige a permissão connect_channel." : !mudou ? "Nada mudou ainda." : null;

  return (
    <div className="hugi-canais-gaveta hugi-canais-perfil">
      {/*
        O aviso vem ANTES dos campos. Nome e foto não são "como este cliente vê o bot": são o bot, para
        todo mundo que já conversou com ele. Num painel multi-tenant isso parece configuração local e não é.
      */}
      <p className="hugi-canais-atencao">
        Isto muda o bot <strong>para todo mundo</strong> que conversa com ele, não só para a sua equipe.
      </p>

      <Campo rotulo="Nome" ajuda="Aparece no topo da conversa e na lista de conversas." valor={atual.nome} limite={limites?.nome}
        desabilitado={!podeEditar || salvando} aoMudar={(v) => setRascunho({ ...atual, nome: v })} />
      <Campo rotulo="Descrição curta" ajuda="Fica no perfil do bot, ao lado do nome." valor={atual.descricaoCurta} limite={limites?.descricaoCurta}
        desabilitado={!podeEditar || salvando} aoMudar={(v) => setRascunho({ ...atual, descricaoCurta: v })} />
      <Campo rotulo="Descrição" ajuda="Aparece na conversa VAZIA, antes de a pessoa mandar a primeira mensagem — é o texto que convence alguém a começar."
        valor={atual.descricao} limite={limites?.descricao} linhas={3} desabilitado={!podeEditar || salvando}
        aoMudar={(v) => setRascunho({ ...atual, descricao: v })} />

      <div className="hugi-canais-campo">
        <span className="hugi-canais-rotulo">Foto <Dica texto="PNG, JPEG ou WebP. Trocar a foto vale para todos os canais deste bot." /></span>
        <span className="hugi-canais-botoes">
          <label className={`v2-botao is-pequeno${podeEditar && !salvando ? "" : " is-inerte"}`}>
            Escolher imagem
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="v2-sr"
              disabled={!podeEditar || salvando}
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                // Limpa o input: escolher o MESMO arquivo de novo, depois de um erro, não dispara `change`.
                e.target.value = "";
                if (arquivo) void enviarFoto(arquivo);
              }}
            />
          </label>
          <button
            type="button"
            className="v2-botao is-pequeno"
            disabled={!podeEditar || salvando}
            onClick={() => {
              setSalvando(true);
              void removerFotoDoBot(c.id).then((r) => {
                setSalvando(false);
                setRecado(r.ok ? { tipo: "ok", texto: "Foto removida." } : { tipo: "erro", texto: mensagemDaFalha(r.falha) });
              });
            }}
          >
            Remover foto
          </button>
        </span>
      </div>

      {/* Comandos: mostrados, não editáveis ainda — o §3 da spec 07 não especifica a edição. */}
      <div className="hugi-canais-campo">
        <span className="hugi-canais-rotulo">Comandos do menu “/”</span>
        {atual.comandos.length === 0 ? (
          <p className="hugi-canais-fraco">
            Nenhum comando cadastrado. Editar comandos pelo Hugi ainda não existe — hoje é no <strong>@BotFather</strong>, com{" "}
            <code>/setcommands</code>.
          </p>
        ) : (
          <ul className="hugi-canais-fraco">
            {atual.comandos.map((cmd) => (
              <li key={cmd.comando}><code>/{cmd.comando}</code> — {cmd.descricao}</li>
            ))}
          </ul>
        )}
      </div>

      {/* O que a Bot API NÃO permite: visível, com onde se muda. */}
      <p className="hugi-canais-fraco hugi-canais-quadro">
        <strong>Só no @BotFather:</strong> o usuário (<code>{c.numeroExibicao ?? "@bot"}</code>) não muda depois de criado; o token se
        regenera com <code>/token</code>; privacidade e modo inline ficam em <code>/setprivacy</code> e <code>/setinline</code>.
      </p>

      {recado ? <p className={recado.tipo === "ok" ? "hugi-canais-recado" : "hugi-canais-recado is-erro"}>{recado.texto}</p> : null}

      <span className="hugi-canais-botoes">
        <button type="button" className="v2-botao is-primario" disabled={salvando || motivo !== null}
          {...(motivo ? { "aria-describedby": idDoMotivo } : {})} onClick={() => void salvar()}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        {mudou ? <button type="button" className="v2-botao" onClick={() => setRascunho(null)}>Descartar</button> : null}
        {motivo ? <Motivo id={idDoMotivo}>{motivo}</Motivo> : null}
      </span>
    </div>
  );
}

function mensagemDaFalha(falha: FalhaDaApi | undefined, generico?: string): string {
  // A mensagem do servidor primeiro: ela já vem em português e já diz o que fazer. O genérico só
  // quando não há nada melhor — "não deu para salvar" estaria errado para uma ação que não salva nada.
  const doServidor = (falha?.erro as { mensagem?: unknown } | undefined)?.mensagem;
  if (typeof doServidor === "string" && doServidor) return doServidor;
  return generico ?? "Não deu para salvar. Tente de novo.";
}

function Campo(props: {
  readonly rotulo: string;
  readonly ajuda: string;
  readonly valor: string;
  readonly limite?: number | undefined;
  readonly linhas?: number | undefined;
  readonly desabilitado: boolean;
  readonly aoMudar: (v: string) => void;
}) {
  const usados = [...props.valor].length;
  const estourou = props.limite !== undefined && usados > props.limite;

  return (
    <label className="hugi-canais-campo">
      <span className="hugi-canais-rotulo">{props.rotulo} <Dica texto={props.ajuda} /></span>
      {props.linhas ? (
        <textarea value={props.valor} rows={props.linhas} disabled={props.desabilitado} onChange={(e) => props.aoMudar(e.target.value)} className="campo" />
      ) : (
        <input type="text" value={props.valor} disabled={props.desabilitado} onChange={(e) => props.aoMudar(e.target.value)} className="campo" />
      )}
      {/*
        O contador conta CARACTERES, não bytes nem unidades UTF-16: o limite do Telegram é de
        caracteres, e `"👍".length` é 2.
      */}
      {props.limite === undefined ? null : (
        <span className={estourou ? "hugi-canais-contador is-erro" : "hugi-canais-contador"}>{usados}/{props.limite}</span>
      )}
    </label>
  );
}

/**
 * O editor do apelido (migration 0055). Inline no cartão, porque é uma palavra: abrir modal para
 * digitar "Recepção" é atrito maior que a tarefa.
 *
 * Vazio LIMPA — e a frase ao lado do campo diz isso. O teto de 60 é o mesmo da CHECK do banco.
 */
function EditorDeApelido(props: {
  readonly conexaoId: string;
  readonly atual: string;
  readonly aoFechar: () => void;
  readonly aoFalhar: (mensagem: string) => void;
}) {
  const cliente = useQueryClient();
  const [valor, setValor] = useState(props.atual);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    const r = await apelidarConexao(cliente, props.conexaoId, valor);
    setSalvando(false);
    if (r.ok) props.aoFechar();
    else props.aoFalhar(r.falha ? mensagemDaFalha(r.falha) : "Não deu para salvar o apelido.");
  }

  return (
    <form
      className="hugi-canais-apelido"
      onSubmit={(e) => {
        e.preventDefault();
        void salvar();
      }}
    >
      <label className="v2-sr" htmlFor={`apelido-${props.conexaoId}`}>Apelido da conexão</label>
      <input
        id={`apelido-${props.conexaoId}`}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        maxLength={60}
        placeholder="Recepção, Financeiro…"
        autoFocus
        // Enter salva, explicitamente: a submissão implícita NÃO aconteceu no navegador de verdade
        // (2026-09-03), e foi assim que um apelido entrou dobrado no ensaio.
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void salvar();
          }
          if (e.key === "Escape") props.aoFechar();
        }}
        className="campo"
      />
      <button type="button" className="v2-botao is-pequeno is-primario" disabled={salvando} onClick={() => void salvar()}>
        {salvando ? "Salvando…" : "Salvar"}
      </button>
      <Dica texto="É o que a equipe lê no cartão da conversa e no relatório. Vazio volta a mostrar o número." />
    </form>
  );
}
